<template>
  <div class="jobs-filters">
    <div class="jobs-filters__bar">
      <div class="jobs-filters__search">
        <GjIcon
          class="jobs-filters__search-icon"
          name="Search"
          size="20"
        />
        <b-form-input
          :value="value"
          type="search"
          class="jobs-filters__input"
          placeholder="Search jobs"
          aria-label="Search by job ID, file path or tag"
          @input="$emit('input', $event)"
        />
        <a
          v-if="value"
          class="jobs-filters__clear"
          aria-label="Clear search"
          @click="$emit('input', '')"
        >
          <GjIcon
            name="Close"
            size="18"
          />
        </a>
      </div>

      <b-button
        variant="bg-white"
        class="jobs-filters__icon-btn"
        :class="{ 'jobs-filters__icon-btn--on': hasStatusFilter }"
        :disabled="!statusOptions.length"
        :aria-label="filterLabel"
        :aria-expanded="sheetOpen ? 'true' : 'false'"
        @click="sheetOpen = true"
      >
        <GjIcon
          name="Filters"
          size="18"
        />
        <span
          v-if="hasStatusFilter"
          class="jobs-filters__badge"
        />
      </b-button>

      <b-button
        variant="bg-white"
        class="jobs-filters__icon-btn"
        :disabled="loading"
        aria-label="Refresh jobs"
        @click="$emit('refresh')"
      >
        <GjIcon
          name="Refresh"
          size="18"
        />
      </b-button>
    </div>

    <!-- Only the active filter is spelled out, so the default state stays one row tall. -->
    <div
      v-if="hasStatusFilter"
      class="jobs-filters__active"
    >
      <button
        type="button"
        class="jobs-filters__pill"
        @click="clearStatus"
      >
        <span class="jobs-filters__pill-text">{{ activeStatusText }}</span>
        <GjIcon
          name="Close"
          size="14"
        />
        <span class="sr-only">Clear status filter</span>
      </button>
    </div>

    <job-status-filter-sheet
      v-model="sheetOpen"
      :status="status"
      :status-options="statusOptions"
      @update:status="$emit('update:status', $event)"
    />
  </div>
</template>

<script>
import JobStatusFilterSheet from './JobStatusFilterSheet.vue'

/**
 * Mobile filter bar for the jobs list: a full width search field, plus the status
 * filter behind a bottom sheet.
 *
 * The statuses used to be rendered as chips right here. There are a dozen of them,
 * they wrapped onto three or four lines, and since this bar is sticky they ate the
 * top third of the screen on every scroll. Now the bar is a single row and the
 * selected status is echoed underneath it - one tappable pill that also clears it.
 */
export default {
  name: 'JobsMobileFilters',
  components: {
    JobStatusFilterSheet,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    status: {
      type: Number,
      default: 0,
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
    // The id meaning "no status filter" (`Any Status`).
    allStatuses: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sheetOpen: false,
    }
  },
  computed: {
    hasStatusFilter() {
      return this.status !== this.allStatuses
    },
    activeStatusText() {
      return this.statusOptions.find(option => option.id === this.status)?.value || ''
    },
    filterLabel() {
      return this.hasStatusFilter
        ? `Filter by status, ${this.activeStatusText} selected`
        : 'Filter by status'
    },
  },
  methods: {
    clearStatus() {
      this.$emit('update:status', this.allStatuses)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables/variables';

.jobs-filters {
  position: sticky;
  top: 4.45rem; // height of the fixed top navbar
  z-index: 2;
  padding: 10px 0 8px;
  background-color: $gray-123;
  border-bottom: 1px solid $gray-1234;

  &__bar {
    display: flex;
    align-items: center;
  }

  &__search {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    align-items: center;
  }

  &__search-icon {
    position: absolute;
    left: 12px;
    color: $blue-800;
    pointer-events: none;
  }

  &__input {
    padding-left: 40px;
    padding-right: 36px;

    // iOS zooms the page in when a focused input is under 16px.
    font-size: 16px;

    &::-webkit-search-cancel-button {
      display: none;
    }
  }

  &__clear {
    position: absolute;
    right: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    color: $blue-800;
  }

  &__icon-btn {
    position: relative;
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    min-width: 42px;
    height: 42px;
    padding: 0;
    margin-left: 8px;
    margin-right: 0 !important;

    &--on {
      border-color: $primary;
      color: $primary;
    }
  }

  // A dot rather than a count: status is the only filter behind the sheet, so all it
  // has to say is "something is filtered".
  &__badge {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 8px;
    height: 8px;
    border: 2px solid $white;
    border-radius: 50%;
    background-color: $primary;
  }

  &__active {
    display: flex;
    margin-top: 8px;
  }

  &__pill {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    height: 28px;
    padding: 0 10px;
    border: 1px solid $primary;
    border-radius: 14px;
    background-color: $blue-400;
    font-size: 12px;
    font-weight: 600;
    color: $primary;
  }

  &__pill-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-right: 6px;
  }
}
</style>
